import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Command } from 'cmdk';
import * as Dialog from '@radix-ui/react-dialog';
import { CornerDownLeft, Search, User, KeyRound } from 'lucide-react';
import { useSearch } from '@/lib/queries';
import { CategoryIcon, StatusDot } from './ui';
import type { NavItem } from './nav';

function useDebounced<T>(value: T, ms: number) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** Ctrl/⌘ K: jump anywhere, or find any asset (tag, serial, model) or person. */
export function CommandPalette({ open, onOpenChange, nav, canSearch }: { open: boolean; onOpenChange: (o: boolean) => void; nav: NavItem[]; canSearch: boolean }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const q = useDebounced(query, 180);
  const search = useSearch(q, canSearch && open);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const go = (to: string) => {
    onOpenChange(false);
    navigate(to);
  };

  const results = search.data;
  const showResults = canSearch && q.trim().length >= 2;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="animate-fade modal-overlay fixed inset-0 z-50" />
        <Dialog.Content className="modal-pop fixed top-[12vh] left-1/2 z-50 w-[calc(100vw-2rem)] max-w-xl -translate-x-1/2 overflow-hidden rounded-[var(--radius-lg)] border border-line bg-surface shadow-[var(--shadow-pop)] focus:outline-none">
          <Dialog.Title className="sr-only">Command palette</Dialog.Title>
          <Dialog.Description className="sr-only">Search assets and people, or jump to a page.</Dialog.Description>
          <Command shouldFilter={!showResults} loop label="Command palette">
            <div className="flex items-center gap-2 border-b border-line px-4">
              <Search aria-hidden className="size-4 text-fg-3" />
              <Command.Input
                autoFocus
                value={query}
                onValueChange={setQuery}
                placeholder={canSearch ? 'Search asset tag, serial, model, person or licence…' : 'Go to…'}
                className="h-12 w-full bg-transparent text-sm text-fg outline-none placeholder:text-fg-3"
              />
              {search.isFetching && <span className="size-1.5 animate-[pulse-dot_1s_ease-in-out_infinite] rounded-full bg-brand" aria-label="Searching" />}
            </div>
            <Command.List className="max-h-[min(60vh,420px)] overflow-y-auto p-2 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-2xs [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:tracking-[0.08em] [&_[cmdk-group-heading]]:text-fg-3 [&_[cmdk-group-heading]]:uppercase">
              <Command.Empty className="px-3 py-8 text-center text-[13px] text-fg-2">
                {showResults && search.isFetching ? 'Searching…' : 'No matches.'}
              </Command.Empty>

              {showResults && results && results.assets.length > 0 && (
                <Command.Group heading="Assets">
                  {results.assets.map((a) => (
                    <Item key={a.id} value={`asset-${a.id}`} onSelect={() => go(`/assets/${a.id}`)}>
                      <CategoryIcon type={a.assetType} className={`size-4 ${a.isLaptop ? 'text-brand' : 'text-fg-3'}`} />
                      <span className="mono text-[13px]">{a.assetTag}</span>
                      <span className="min-w-0 flex-1 truncate text-fg-2">{a.name}</span>
                      <StatusDot status={a.status} />
                      {a.assignee && <span className="hidden max-w-[140px] truncate text-xs text-fg-3 sm:inline">{a.assignee.displayName}</span>}
                    </Item>
                  ))}
                </Command.Group>
              )}
              {showResults && results && results.employees.length > 0 && (
                <Command.Group heading="People">
                  {results.employees.map((e) => (
                    <Item key={e.id} value={`emp-${e.id}`} onSelect={() => go(`/employees/${e.id}`)}>
                      <User aria-hidden className="size-4 text-fg-3" />
                      <span className="min-w-0 flex-1 truncate">{e.displayName}</span>
                      <span className="mono text-xs text-fg-3">{e.employeeCode}</span>
                      <span className="hidden text-xs text-fg-3 sm:inline">{e.deviceCount} device{e.deviceCount === 1 ? '' : 's'}</span>
                    </Item>
                  ))}
                </Command.Group>
              )}

              {showResults && results?.licenses && results.licenses.length > 0 && (
                <Command.Group heading="Software & licences">
                  {results.licenses.map((l) => (
                    <Item key={l.id} value={`lic-${l.id}`} onSelect={() => go(`/software/licenses/${l.id}`)}>
                      <KeyRound aria-hidden className="size-4 text-fg-3" />
                      <span className="mono text-[13px]">{l.licenseTag}</span>
                      <span className="min-w-0 flex-1 truncate text-fg-2">{l.productName}</span>
                      {l.licenseType && <span className="hidden max-w-[140px] truncate text-xs text-fg-3 sm:inline">{l.licenseType}</span>}
                    </Item>
                  ))}
                </Command.Group>
              )}

              {!showResults && (
                <Command.Group heading="Go to">
                  {nav.map((n) => (
                    <Item key={n.to} value={`${n.label} ${n.keywords ?? ''}`} onSelect={() => go(n.to)}>
                      <n.icon aria-hidden className="size-4 text-fg-3" />
                      <span className="flex-1">{n.label}</span>
                      {n.shortcut && <span className="kbd">{n.shortcut}</span>}
                    </Item>
                  ))}
                </Command.Group>
              )}
            </Command.List>
            <div className="flex items-center gap-3 border-t border-line px-4 py-2 text-2xs text-fg-3">
              <span className="flex items-center gap-1">
                <CornerDownLeft aria-hidden className="size-3" /> open
              </span>
              <span>↑↓ move</span>
              <span>esc close</span>
            </div>
          </Command>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Item({ children, value, onSelect }: { children: React.ReactNode; value: string; onSelect: () => void }) {
  return (
    <Command.Item
      value={value}
      onSelect={onSelect}
      className="flex h-10 cursor-pointer items-center gap-3 rounded-[var(--radius-md)] px-2.5 text-[13px] text-fg data-[selected=true]:bg-surface-2"
    >
      {children}
    </Command.Item>
  );
}
